import React, { useCallback, useEffect, useRef } from 'react'
import HTMLFlipBook from 'react-pageflip';
import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import '../PDFFlipbook/PDFFlipbook.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PDFPage = React.forwardRef((props, ref) => {
    return (
        <div className="demoPage" ref={ref}>
            <Page
                pageNumber={props.pageNumber}
                width={props.width}
                height={props.height}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onLoadSuccess={props.onLoadSuccess}
            />
        </div>
    );
});

PDFPage.displayName = 'PDFPage';

// Modal Component with Bootstrap classes
const Modal = React.forwardRef(({ isOpen, onClose, children }, ref) => {
    if (!isOpen) return null;

    return (
        <div
            ref={ref}
            className="flbook modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
            tabIndex="-1"
        >
            <div className="modal-dialog modal-xl modal-dialog-centered mw-80">
                <div className="modal-content">
                    <div className="modal-header border-0 pb-0">
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    {/*<div className="modal-body p-0 d-flex justify-content-center align-items-center">*/}
                    <div className="modal-body p-0 d-flex justify-content-center align-items-center"
                        style={{ overflow: 'hidden', padding: '20px' }}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
});

Modal.displayName = 'Modal';

function PDFFlipbook({ pdfUrl, catalogSize, onClose, isOpen }) {
    const [numPages, setNumPages] = useState(null);
    const [pdfDocument, setPdfDocument] = useState(null);

    // Parse catalogSize to get width and height
    const getSizeFromCatalog = useCallback(() => {
        if (!catalogSize) return { width: 1080, height: 607 };

        try {
            const [width, height] = catalogSize.split('*').map(Number);
            return {
                width: width || 1080,
                height: height || 607
            };
        } catch (error) {
            console.error('Error parsing catalogSize:', error);
            return { width: 1080, height: 607 };
        }
    }, [catalogSize]);

    const defaultSize = getSizeFromCatalog();
    const [bookSize, setBookSize] = useState(defaultSize);
    const firstPageLoaded = useRef(false);
    const modalRef = useRef();

    const calculateOptimalSize = useCallback((pdfWidth, pdfHeight) => {
        if (!modalRef.current) return defaultSize;

        const modalBody = modalRef.current.querySelector('.modal-body');
        if (!modalBody) return defaultSize;

        const availableWidth = modalBody.clientWidth - 0;
        const availableHeight = modalBody.clientHeight - 0;
        console.log("availableWidth, availableHeight", availableWidth, availableHeight )


        // Calculate scale based on available space and PDF aspect ratio
        const widthRatio = availableWidth / pdfWidth;
        const heightRatio = availableHeight / pdfHeight;
        const scale = Math.min(widthRatio, heightRatio, 1);
        console.log("widthRatio, heightRatio",widthRatio, heightRatio,scale)

        const scaledWidth = Math.floor(pdfWidth * scale);
        const scaledHeight = Math.floor(pdfHeight * scale);

        // Return scaled dimensions that fit within available space
        //return {
        //    width: Math.min(scaledWidth, availableWidth),
        //    height: Math.min(scaledHeight, availableHeight)
        //};
        return {
            width: pdfWidth,
            height: pdfHeight
        };
    }, [defaultSize]);

    const onFirstPageLoadSuccess = useCallback((page) => {
        if (firstPageLoaded.current) return;

        const { width: pdfWidth, height: pdfHeight } = page.getViewport({ scale: 1 });
        const optimalSize = calculateOptimalSize(pdfWidth, pdfHeight);

        setBookSize(optimalSize);
        firstPageLoaded.current = true;
    }, [calculateOptimalSize]);

    const onSubsequentPageLoad = useCallback(() => { }, []);

    function onDocumentLoadSuccessWrapper(pdf) {
        setPdfDocument(pdf);
        setNumPages(pdf.numPages);
    }

    useEffect(() => {
        if (!isOpen) {
            setPdfDocument(null);
            setNumPages(null);
            firstPageLoaded.current = false;
            setBookSize(defaultSize);
        }
    }, [isOpen, defaultSize]);

    // Update size when catalogSize changes
    useEffect(() => {
        const newSize = getSizeFromCatalog();
        setBookSize(newSize);
    }, [catalogSize, getSizeFromCatalog]);

    // If PDF is still loading or failed to load
    if (!pdfDocument && pdfUrl) {
        return (
            <Modal isOpen={isOpen} onClose={onClose}>
                <div className='w-full flex flex-col gap-5 justify-center items-center p-4'>
                    <h1 className='text-3xl text-black text-center font-bold'>Loading PDF...</h1>
                    <Document
                        file={pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccessWrapper}
                        loading={<div className="text-black">Loading PDF...</div>}
                        error={<div className="text-danger">Failed to load PDF</div>}
                    >
                        <div className="text-white">Processing PDF...</div>
                    </Document>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} ref={modalRef}>
            <div className="flipbook-modal d-flex justify-content-center align-items-center w-100 h-100"
                style={{ overflow: 'hidden' }}>
                <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccessWrapper}
                    loading={<div className="flipbook-loading text-white">Loading PDF...</div>}
                    error={<div className="flipbook-error text-danger">Failed to load PDF</div>}
                >
                    {numPages && (
                        <HTMLFlipBook
                            width={bookSize.width}
                            height={bookSize.height}
                            style={{
                                width: bookSize.width,
                                height: bookSize.height,
                                margin: "0 auto",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            {Array.from(new Array(numPages), (el, index) => (
                                <PDFPage
                                    key={`page_${index + 1}`}
                                    pageNumber={index + 1}
                                    totalPages={numPages}
                                    width={bookSize.width}
                                    height={bookSize.height}
                                    onLoadSuccess={index === 0 ? onFirstPageLoadSuccess : onSubsequentPageLoad}
                                    className="flipbook-page"
                                />
                            ))}
                        </HTMLFlipBook>
                    )}
                </Document>
            </div>
        </Modal>
    );
}

export default PDFFlipbook;